import React from 'react';

/**
 * Skeleton - Base skeleton loader component with shimmer animation
 *
 * @param {string} className - Additional CSS classes
 * @param {string} variant - 'text' | 'circular' | 'rectangular' | 'rounded-sm'
 * @param {string|number} width - Width of the skeleton
 * @param {string|number} height - Height of the skeleton
 * @param {boolean} animate - Whether to show animation (default: true)
 */
function Skeleton({
  className = '',
  variant = 'text',
  width,
  height,
  animate = true
}) {
  const baseClasses = `
    bg-gray-200 dark:bg-gray-700
    ${animate ? 'animate-pulse' : ''}
  `;

  const variantClasses = {
    text: 'rounded-sm',
    circular: 'rounded-full',
    rectangular: '',
    rounded: 'rounded-lg'
  };

  const style = {
    width: width || (variant === 'circular' ? height : '100%'),
    height: height || (variant === 'text' ? '1em' : 'auto')
  };

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={style}
      aria-hidden="true"
    />
  );
}

/**
 * ConversationListSkeleton - Skeleton for the sidebar conversation list
 */
function ConversationListSkeleton({ count = 4 }) {
  return (
    <div className="space-y-4 px-2">
      {/* Date group header */}
      <div className="px-3 py-1">
        <Skeleton width={60} height={12} className="rounded-sm" />
      </div>

      {/* Conversation items */}
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2">
          <Skeleton variant="circular" width={16} height={16} />
          <Skeleton variant="text" height={16} className="flex-1" />
        </div>
      ))}

      {/* Second group */}
      <div className="px-3 py-1 mt-4">
        <Skeleton width={80} height={12} className="rounded-sm" />
      </div>

      {Array.from({ length: 2 }).map((_, i) => (
        <div key={`g2-${i}`} className="flex items-center gap-3 px-3 py-2">
          <Skeleton variant="circular" width={16} height={16} />
          <Skeleton variant="text" height={16} className="flex-1" />
        </div>
      ))}
    </div>
  );
}

/**
 * MessageSkeleton - Skeleton for a single message bubble
 */
function MessageSkeleton({ isUser = false }) {
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`flex items-start gap-3 max-w-[85%] ${isUser ? 'flex-row-reverse' : ''}`}>
        {/* Avatar */}
        <Skeleton variant="circular" width={32} height={32} />

        {/* Message content */}
        <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} space-y-2`}>
          <div className={`rounded-2xl px-4 py-3 ${isUser ? 'bg-gray-100 dark:bg-gray-800' : ''}`}>
            <div className="space-y-2">
              <Skeleton variant="text" width={200} height={14} />
              <Skeleton variant="text" width={280} height={14} />
              <Skeleton variant="text" width={160} height={14} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * MessageHistorySkeleton - Skeleton for a list of messages
 */
function MessageHistorySkeleton({ count = 3 }) {
  return (
    <div className="space-y-6">
      {Array.from({ length: count }).map((_, i) => (
        <MessageSkeleton key={i} isUser={i % 2 === 0} />
      ))}
    </div>
  );
}

/**
 * ChatInputSkeleton - Skeleton for the chat input area
 */
function ChatInputSkeleton() {
  return (
    <div className="flex items-end gap-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3">
      <Skeleton variant="circular" width={40} height={40} />
      <Skeleton variant="rounded" height={40} className="flex-1" />
      <Skeleton variant="circular" width={40} height={40} />
    </div>
  );
}

/**
 * PageSkeleton - Full page skeleton with all components
 */
function PageSkeleton() {
  return (
    <div className="flex h-screen animate-pulse">
      {/* Sidebar skeleton */}
      <div className="w-64 border-r border-gray-200 dark:border-gray-700 p-4 space-y-4">
        <Skeleton variant="rounded" height={40} />
        <Skeleton variant="rounded" height={36} />
        <ConversationListSkeleton count={3} />
      </div>

      {/* Main content skeleton */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <Skeleton variant="text" width={150} height={24} />
          <div className="flex gap-2">
            <Skeleton variant="rounded" width={120} height={32} />
            <Skeleton variant="circular" width={32} height={32} />
          </div>
        </div>

        {/* Messages area */}
        <div className="flex-1 p-4">
          <div className="max-w-7xl mx-auto">
            <MessageHistorySkeleton count={3} />
          </div>
        </div>

        {/* Input area */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="max-w-3xl mx-auto">
            <ChatInputSkeleton />
          </div>
        </div>
      </div>
    </div>
  );
}

export {
  Skeleton,
  ConversationListSkeleton,
  MessageSkeleton,
  MessageHistorySkeleton,
  ChatInputSkeleton,
  PageSkeleton
};

export default Skeleton;
